import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, Pressable, Modal } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import moment from 'moment';
import {
    dataSourceJobsAtom, selectedTeamAtom, selectedVersionAtom,
    dataSourceVersionAtom,
    selectedJobAtom, selectedJobDataAtom, selectedTab,
    jobOpenCommitmentsAtom, jobUserDataAtom, jobCostDetailAtom,
    jobCostDetailCondensedAtom, k2DataAtom, jobHEETDataAtom, phasedForecastAtom,
} from '../store/company';
import {
    loadJobRoster, loadCostHistoryCombined, loadJobLaborIntervals,
    loadUserDataRaw, updateUserDataArray,
    setSelectedJobStorage, setSelectedJobDetailsStorage, setClosedJobMode,
} from '../db/company';
import { LaborInterface } from './burnToolPhased';

// Left (frozen) column widths — must sum to LEFT_PANEL.
// COL_JOB includes the 20px expand caret that sits at the start of each row.
const COL_JOB = 74, COL_PROJ = 180, COL_START = 60, COL_STOP = 60, COL_CV = 70, COL_BACK = 78;
const LEFT_PANEL = COL_JOB + COL_PROJ + COL_START + COL_STOP + COL_CV + COL_BACK;

// Timeline
const PX_PER_MONTH = 64;
const ROW_H = 26;
const BAR_H = 14;

// Colors
const C_ACTIVE = '#2563EB';
const C_PENDING = '#D97706';
const C_TODAY = '#DC2626';
const C_NEG = '#B91C1C';
const C_BORDER = '#E5E7EB';
const C_HEADER = '#F3F4F6';
// Contract span behind the labor bars when a job has intervals
const C_RAIL = '#CBD5E1';
// Zebra band for alternating project rows (lighter than the header gray)
const C_BAND = '#F4F7FB';

// Date-only fields are naive datetimes on a Central-time server; parse as UTC
// (no .local()) so the calendar day never shifts. A 1900-01-01 value is JDE's
// "not set yet" sentinel (esp. Actual_Complete_Date on in-flight jobs) — treat
// anything at/around the epoch as missing.
const d = (v) => (v ? moment.utc(v) : null);
const isReal = (m) => m && m.isValid() && m.year() > 1901;

const formatMoney = (n) => {
    const num = Number(n);
    if (!n || isNaN(num)) return '—';
    const a = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    if (a >= 1000000) return sign + '$' + (a / 1000000).toFixed(2) + 'M';
    if (a >= 1000) return sign + '$' + Math.round(a / 1000) + 'K';
    return sign + '$' + Math.round(a);
};
const fmtDate = (m) => (isReal(m) ? m.format('M/D/YY') : '—');

// Same guarded date input the top-menu date modal uses (sane-range clamp).
const DateInput = ({ value, onValueChange, disabled }) => {
    const handleChange = (e) => {
        const inputValue = e.target.value;
        if (!inputValue) return;
        if (moment(inputValue, 'YYYY-MM-DD', true).isValid()
            && moment(inputValue).isAfter('2020-01-01') && moment(inputValue).isBefore('2040-01-01')) {
            onValueChange(inputValue);
        }
    };
    return (
        <input
            type="date"
            value={value ? moment(value).format('YYYY-MM-DD') : ''}
            onChange={handleChange}
            disabled={disabled}
            style={{
                width: 150, height: 34, fontSize: 12, fontWeight: 600, padding: '4px 6px',
                borderRadius: 6, border: '1px solid #ccc',
                backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
            }}
        />
    );
};

const TeamGantt = () => {
    const [jobsRaw] = useAtom(dataSourceJobsAtom);
    const [selectedTeam] = useAtom(selectedTeamAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [versions] = useAtom(dataSourceVersionAtom);

    // Job selection + navigation — clicking a project selects it and jumps to Forecast.
    const [, setSelectedJob] = useAtom(selectedJobAtom);
    const [, setSelectedJobDetails] = useAtom(selectedJobDataAtom);
    const [, setTab] = useAtom(selectedTab);
    const [, setJobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [, setJobUserData] = useAtom(jobUserDataAtom);
    const [, setJobCostDetail] = useAtom(jobCostDetailAtom);
    const [, setJobCostDetailCondensed] = useAtom(jobCostDetailCondensedAtom);
    const [, setK2Data] = useAtom(k2DataAtom);
    const [, setJobHEETData] = useAtom(jobHEETDataAtom);
    const [, setRegular] = useAtom(phasedForecastAtom);

    const jobs = Array.isArray(jobsRaw) ? jobsRaw : [];

    const [teamFilter, setTeamFilter] = useState(selectedTeam || 'ALL');
    const [pmFilter, setPmFilter] = useState('ALL');
    const [superFilter, setSuperFilter] = useState('ALL');
    const [rosterByJob, setRosterByJob] = useState({});
    const [rosterLoaded, setRosterLoaded] = useState(false);

    // Staffed labor windows per job (one entry per mobilization).
    const [intervalsByJob, setIntervalsByJob] = useState({});
    const [intervalsLoaded, setIntervalsLoaded] = useState(false);
    const [showIntervals, setShowIntervals] = useState(true);

    // Expand state: crew (actual-labor supers/foremen) loaded per job on demand.
    const [expanded, setExpanded] = useState({});   // { [BU_ID]: true }
    const [crewByJob, setCrewByJob] = useState({});  // { [BU_ID]: {loading, error, people[]} }

    // Edit-dates modal (opened by clicking a row's Start/Stop cells) and the
    // session-local date overrides applied to rows after a successful save.
    const [dateModal, setDateModal] = useState(null); // { id, name, loading, saving, locked, error, start, end, mech, orig }
    const [dateOverrides, setDateOverrides] = useState({}); // { [BU_ID]: { start, end } }

    // Dates are editable only on the live (highest) version, matching the
    // main.js version-lock rule; a per-job PCL lock is checked on modal open.
    const versionEditable = useMemo(() => {
        if (!selectedVersion || !Array.isArray(versions) || !versions.length) return false;
        let highest = 0;
        versions.forEach((v) => { if (highest < v.Version_GUID) highest = v.Version_GUID; });
        const cur = versions.find((v) => v.Version_GUID == selectedVersion);
        if (!cur || selectedVersion < highest) return false;
        return !String(cur.Version_Type || '').includes('locked');
    }, [versions, selectedVersion]);

    useEffect(() => { if (selectedTeam) setTeamFilter(selectedTeam); }, [selectedTeam]);

    // Bulk value + supers + billed-to-date. Degrades gracefully if not deployed.
    useEffect(() => {
        let cancelled = false;
        loadJobRoster()
            .then((res) => {
                if (cancelled) return;
                const map = {};
                (res && res.data ? res.data : []).forEach((r) => {
                    if (r.Job_Number != null) map[String(r.Job_Number)] = r;
                });
                setRosterByJob(map);
                setRosterLoaded(true);
            })
            .catch(() => setRosterLoaded(true));
        return () => { cancelled = true; };
    }, []);

    // Labor intervals for every job in one call. Jobs whose burn tool has gaps
    // come back as several rows; jobs with none simply aren't in the map and
    // keep the single planned-span bar. Degrades gracefully if not deployed.
    useEffect(() => {
        let cancelled = false;
        setIntervalsLoaded(false);
        loadJobLaborIntervals(selectedVersion ? { versionGuid: selectedVersion } : {})
            .then((res) => {
                if (cancelled) return;
                const map = {};
                (res && res.data ? res.data : []).forEach((r) => {
                    const start = d(r.Interval_Start), end = d(r.Interval_End);
                    if (!isReal(start) || !isReal(end)) return;
                    const key = String(r.BU_ID);
                    (map[key] = map[key] || []).push({
                        start, end,
                        weeks: Number(r.Staffed_Weeks) || 0,
                        peakCrew: Number(r.Peak_Crew) || 0,
                        hours: Number(r.Total_Hours) || 0,
                        cost: Number(r.Total_Cost) || 0,
                        phased: !!Number(r.Is_Phased),
                    });
                });
                Object.keys(map).forEach((k) => map[k].sort((a, b) => a.start.valueOf() - b.start.valueOf()));
                setIntervalsByJob(map);
                setIntervalsLoaded(true);
            })
            .catch(() => setIntervalsLoaded(true));
        return () => { cancelled = true; };
    }, [selectedVersion]);

    // Shape every job into a row (independent of the active filters).
    const allRows = useMemo(() => {
        return jobs
            .filter((j) => j.Planned_Start_Date || j.Actual_Start_Date)
            .map((j) => {
                const aStart = d(j.Actual_Start_Date), pStart = d(j.Planned_Start_Date);
                const aComp = d(j.Actual_Complete_Date), pComp = d(j.Planned_Complete_Date);
                let start = isReal(aStart) ? aStart : pStart;
                let end = isReal(aComp) ? aComp : pComp;
                let openEnded = false;
                if (!isReal(end) || (isReal(start) && end.isBefore(start))) {
                    end = isReal(start) ? start.clone().add(1, 'month') : null;
                    openEnded = true;
                }
                // Dates edited through this tab's modal this session win over
                // the (possibly stale) job-master dates.
                const ov = dateOverrides[String(j.BU_ID)];
                if (ov) {
                    const os = d(ov.start), oe = d(ov.end);
                    if (isReal(os)) start = os;
                    if (isReal(oe)) { end = oe; openEnded = false; }
                    if (isReal(start) && isReal(end) && end.isBefore(start)) {
                        end = start.clone().add(1, 'month');
                        openEnded = true;
                    }
                }
                const r = rosterByJob[String(j.BU_ID)] || {};
                // CV = original contract (K2 setup) + approved change-order revenue,
                // matching the forecast's Current Budget contract amount.
                const baseCv = Number(r.Contract_PO_Amount) || Number(r.Estimated_Project_Amount) || null;
                const approvedCOs = Number(r.Approved_CO_Amount) || 0;
                const cv = baseCv != null ? baseCv + approvedCOs : (approvedCOs || null);
                const billed = Number(r.Billed_To_Date) || 0;
                const intervals = intervalsByJob[String(j.BU_ID)] || [];
                return {
                    id: j.BU_ID,
                    raw: j,
                    name: j.BU_Description || 'N/A',
                    pm: j.Project_Manager_Name || 'Unassigned',
                    team: j.Cat_Code_BU_27 || '',
                    // Active/Pending flag. Primary source (Form_Job_Master) puts the
                    // A/P value in Job_Status and the tier in Cat_Code_BU_16; the live
                    // v_BU_Master source carries A/P in Cat_Code_BU_16 and no Job_Status.
                    status: j.Job_Status || j.Cat_Code_BU_16,
                    scope: j.Scope_of_Work || j.BU_Description || '',
                    start, end, openEnded,
                    cv,
                    billed,
                    backlog: cv != null ? cv - billed : null,
                    superintendent: r.Project_Superintendent || '',
                    fieldSupport: r.Field_Support_Specialist || '',
                    intervals,
                };
            })
            .filter((r) => isReal(r.start));
    }, [jobs, rosterByJob, intervalsByJob, dateOverrides]);

    // Cascading filter option lists.
    const teamRows = useMemo(
        () => allRows.filter((r) => teamFilter === 'ALL' || r.team === teamFilter),
        [allRows, teamFilter]);
    const teamOptions = useMemo(
        () => Array.from(new Set(allRows.map((r) => r.team).filter(Boolean))).sort(),
        [allRows]);
    const pmOptions = useMemo(
        () => Array.from(new Set(teamRows.map((r) => r.pm).filter(Boolean))).sort(),
        [teamRows]);
    const superOptions = useMemo(
        () => Array.from(new Set(teamRows
            .filter((r) => pmFilter === 'ALL' || r.pm === pmFilter)
            .map((r) => r.superintendent).filter(Boolean))).sort(),
        [teamRows, pmFilter]);

    const rows = useMemo(() => teamRows
        .filter((r) => pmFilter === 'ALL' || r.pm === pmFilter)
        .filter((r) => superFilter === 'ALL' || r.superintendent === superFilter),
        [teamRows, pmFilter, superFilter]);

    // Group by PM.
    const groups = useMemo(() => {
        const byPm = {};
        rows.forEach((r) => { (byPm[r.pm] = byPm[r.pm] || []).push(r); });
        return Object.keys(byPm).sort((a, b) => a.localeCompare(b)).map((pm) => {
            const items = byPm[pm].sort((a, b) => a.start.valueOf() - b.start.valueOf());
            const totalCv = items.reduce((s, i) => s + (Number(i.cv) || 0), 0);
            return { pm, items, totalCv };
        });
    }, [rows]);

    // Timeline range clamped to a sensible window around today.
    const timeline = useMemo(() => {
        const today = moment.utc().startOf('day');
        let min = today.clone().subtract(1, 'month');
        let max = today.clone().add(3, 'month');
        rows.forEach((r) => {
            if (r.start.isBefore(min)) min = r.start.clone();
            if (r.end && r.end.isAfter(max)) max = r.end.clone();
            // Labor can be planned outside the contract dates — keep it on screen.
            if (showIntervals) r.intervals.forEach((iv) => {
                if (iv.start.isBefore(min)) min = iv.start.clone();
                if (iv.end.isAfter(max)) max = iv.end.clone();
            });
        });
        const rangeStart = min.clone().startOf('month');
        const rangeEnd = max.clone().endOf('month');
        const months = Math.max(rangeEnd.diff(rangeStart, 'months') + 1, 1);
        const xOf = (m) => m.diff(rangeStart, 'months', true) * PX_PER_MONTH;
        const width = months * PX_PER_MONTH;
        const monthLabels = [];
        for (let i = 0; i < months; i++) monthLabels.push(rangeStart.clone().add(i, 'months').format('MMM YY'));
        return { rangeStart, months, width, xOf, monthLabels, todayX: xOf(today) };
    }, [rows, showIntervals]);

    const toggleExpand = (job) => {
        const id = job.id;
        setExpanded((e) => ({ ...e, [id]: !e[id] }));
        if (!crewByJob[id] && !expanded[id]) loadCrew(job);
    };

    // Select the clicked project and jump to its Forecast — mirrors the job
    // picker in topMenu.js (clear cached job data, persist selection, set
    // phased mode) so the Forecast tab loads fresh for this job.
    const selectJobAndForecast = (row) => {
        const job = row.raw;
        if (!job || !job.BU_ID) return;
        setJobOpenCommitments([]);
        setJobUserData([]);
        setJobCostDetail([]);
        setJobCostDetailCondensed([]);
        setK2Data(null);
        setJobHEETData(null);
        setClosedJobMode(false);
        setSelectedJob(job.BU_ID);
        setSelectedJobDetails(job);
        setSelectedJobDetailsStorage(job);
        setSelectedJobStorage(job.BU_ID);
        setRegular(job.Cat_Code_BU_18 == '3' ? 'phased' : 'regular');
        setTab('Forecast');
    };

    // Open the edit-dates modal for one row. Prefills from the row's displayed
    // dates, then loads the job's saved Forecast_User_Data dates (the same
    // startDateActual / completionDateForecast / mechanicallyComplete fields the
    // top-menu date modal edits) which win when present. Also picks up a
    // per-job PCL 'versionLocked' row so locked forecasts stay read-only.
    const openDateModal = (r) => {
        const prefill = {
            start: isReal(r.start) ? r.start.format('YYYY-MM-DD') : '',
            end: (r.end && !r.openEnded && isReal(r.end)) ? r.end.format('YYYY-MM-DD') : '',
            mech: '',
        };
        const base = {
            id: r.id, name: r.name, loading: true, saving: false,
            locked: !versionEditable, error: '',
            ...prefill, orig: { ...prefill },
        };
        if (!selectedVersion) {
            setDateModal({ ...base, loading: false, locked: true, error: 'Select a forecast version to edit dates.' });
            return;
        }
        setDateModal(base);
        loadUserDataRaw({ id: r.id, forecastGuid: selectedVersion })
            .then((res) => {
                const rows = res && res.data ? res.data : [];
                let { start, end, mech } = prefill;
                let jobLock = false;
                rows.forEach((row) => {
                    if (!row) return;
                    if (row.Forecast_Field_Name === 'startDateActual' && row.Forecast_Value) start = row.Forecast_Value;
                    if (row.Forecast_Field_Name === 'completionDateForecast' && row.Forecast_Value) end = row.Forecast_Value;
                    if (row.Forecast_Field_Name === 'mechanicallyComplete' && row.Forecast_Value) mech = row.Forecast_Value;
                    if (row.Forecast_Field_Name === 'versionLocked'
                        && (String(row.Forecast_Value) === 'true' || String(row.Forecast_Value) === String(selectedVersion))) jobLock = true;
                });
                setDateModal((m) => (m && m.id === r.id
                    ? { ...m, loading: false, locked: m.locked || jobLock, start, end, mech, orig: { start, end, mech } }
                    : m));
            })
            .catch(() => setDateModal((m) => (m && m.id === r.id
                ? { ...m, loading: false, error: 'Could not load saved dates — editing the dates shown on the row.' }
                : m)));
    };

    const saveDateModal = async () => {
        const m = dateModal;
        if (!m || m.saving || m.loading || m.locked) return;
        const mk = (fieldName, value) => ({
            fieldName, tab: 'All', rowId: 1,
            version: String(selectedVersion || ''),
            value, id: m.id, lockedVersion: false,
            projectStart: m.start, projectComplete: m.end,
        });
        const changes = [];
        if (m.start && m.start !== m.orig.start) changes.push(mk('startDateActual', m.start));
        if (m.end && m.end !== m.orig.end) changes.push(mk('completionDateForecast', m.end));
        if (m.mech && m.mech !== m.orig.mech) changes.push(mk('mechanicallyComplete', m.mech));
        if (!changes.length) { setDateModal(null); return; }
        setDateModal({ ...m, saving: true });
        await updateUserDataArray(changes);
        setDateOverrides((o) => ({ ...o, [String(m.id)]: { start: m.start, end: m.end } }));
        setDateModal(null);
    };

    // Pull actual labor for one job, keep the superintendents & foremen who
    // actually charged time (same LaborInterface classification the burn tool uses).
    const loadCrew = (job) => {
        const id = job.id;
        if (!selectedVersion) {
            setCrewByJob((c) => ({ ...c, [id]: { error: 'Select a forecast version to load field crew.', people: [] } }));
            return;
        }
        setCrewByJob((c) => ({ ...c, [id]: { loading: true, people: [] } }));
        loadCostHistoryCombined({ id, versionGuid: selectedVersion })
            .then((res) => {
                const detail = res && res.data ? res.data : [];
                const agg = {};
                detail.forEach((row) => {
                    const role = LaborInterface[row.Labor_Classification_Description] || 'OTHER';
                    if (role !== 'SUPERINTENDENT' && role !== 'FOREMAN') return;
                    const name = row.Staff_First_Last_Name || row.Staff_ID || 'Unknown';
                    const key = name + '|' + role;
                    if (!agg[key]) agg[key] = { name, role, title: row.Labor_Classification_Description, hours: 0, cost: 0, first: null, last: null };
                    agg[key].hours += Number(row.Total_Hours) || 0;
                    agg[key].cost += Number(row.Job_Cost) || 0;
                    // Labor date span for the person's Gantt bar
                    const when = d(row.Consolidated_Date || row.GL_Date);
                    if (isReal(when)) {
                        if (!agg[key].first || when.isBefore(agg[key].first)) agg[key].first = when;
                        if (!agg[key].last || when.isAfter(agg[key].last)) agg[key].last = when;
                    }
                });
                const people = Object.values(agg).sort((a, b) => b.cost - a.cost);
                setCrewByJob((c) => ({ ...c, [id]: { people } }));
            })
            .catch(() => setCrewByJob((c) => ({ ...c, [id]: { error: 'Could not load field crew.', people: [] } })));
    };

    // Job-level Excel export of the currently-filtered rows (one row per job),
    // in the same PM-grouped order shown on screen. Numbers stay numeric.
    // ExcelJS (not SheetJS) because freeze panes are a SheetJS Pro-only feature.
    const exportExcel = async () => {
        const ordered = groups.flatMap((g) => g.items);
        if (!ordered.length) return;
        try {
            const ExcelJS = (await import('exceljs')).default;
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet('Team Schedule');

            const headers = ['Job', 'Project', 'PM', 'Team', 'Superintendent', 'Status', 'Start', 'Stop', 'CV Revenue', 'Billed To Date', 'Backlog', 'Labor Intervals', 'Labor Windows', 'Scope'];
            const headerRow = ws.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };

            ordered.forEach((r) => {
                ws.addRow([
                    r.id,
                    r.name,
                    r.pm,
                    r.team,
                    r.superintendent || '',
                    r.status === 'P' ? 'Pending' : 'Active',
                    isReal(r.start) ? r.start.format('M/D/YYYY') : '',
                    (r.end && !r.openEnded) ? r.end.format('M/D/YYYY') : '',
                    r.cv != null ? r.cv : null,
                    r.billed || null,
                    r.backlog != null ? r.backlog : null,
                    r.intervals.length || null,
                    r.intervals.map((iv) => `${iv.start.format('M/D/YY')}–${iv.end.format('M/D/YY')} (${Math.round(iv.peakCrew)} crew)`).join('; '),
                    r.scope || '',
                ]);
            });

            const widths = [10, 37, 21, 13, 21, 10, 11, 11, 14, 14, 14, 9, 44, 46];
            ws.columns.forEach((col, i) => { col.width = widths[i] || 14; });
            ['I', 'J', 'K'].forEach((c) => { ws.getColumn(c).numFmt = '#,##0'; });

            // Freeze the left four ID columns (Job..Team) and the header row
            ws.views = [{ state: 'frozen', xSplit: 4, ySplit: 1 }];
            ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: headers.length } };

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const teamTag = teamFilter === 'ALL' ? 'all-teams' : String(teamFilter).replace(/\s+/g, '-');
            a.download = `team-schedule-${teamTag}-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
            try { window.alert('Export failed: ' + (err?.message || err)); } catch (e) { }
        }
    };

    // ---- cell helpers ----
    const col = (w, extra) => ({ width: w, minWidth: w, paddingLeft: 8, paddingRight: 8, justifyContent: 'center', ...extra });
    const leftPanel = (bg) => ({
        width: LEFT_PANEL, minWidth: LEFT_PANEL, position: 'sticky', left: 0, zIndex: 2,
        backgroundColor: bg, borderRightWidth: 1, borderRightColor: C_BORDER, flexDirection: 'row',
    });

    // One bar per staffed labor window. A job whose burn tool runs straight
    // through gets a single segment and reads like the old contract-span bar;
    // a job with three mobilizations gets three, over a thin rail showing the
    // contract span so the gaps are visible as gaps and not as missing data.
    const bar = (r) => {
        const ivs = showIntervals ? r.intervals : [];
        const color = r.status === 'P' ? C_PENDING : C_ACTIVE;
        const spanLeft = timeline.xOf(r.start);
        const spanW = Math.max((r.end ? timeline.xOf(r.end) : spanLeft) - spanLeft, 6);

        if (!ivs.length) {
            return (
                <View style={{ position: 'relative', height: ROW_H, width: timeline.width }}
                    title={`${fmtDate(r.start)} – ${r.openEnded ? 'open' : fmtDate(r.end)}`
                        + (showIntervals && intervalsLoaded ? ' · no burn tool labor on this version' : '')}>
                    <View style={{
                        position: 'absolute', left: spanLeft, top: (ROW_H - BAR_H) / 2, width: spanW, height: BAR_H,
                        backgroundColor: color, opacity: r.openEnded ? 0.5 : 1, borderRadius: 4,
                        justifyContent: 'center', paddingLeft: 6, paddingRight: 6, overflow: 'hidden',
                    }}>
                        <Text style={{ fontSize: 10, color: '#FFFFFF', whiteSpace: 'nowrap' }}>
                            {formatMoney(r.cv)}{r.openEnded ? ' ▸' : ''}
                        </Text>
                    </View>
                </View>
            );
        }

        return (
            <View style={{ position: 'relative', height: ROW_H, width: timeline.width }}
                title={`${ivs.length} labor interval${ivs.length === 1 ? '' : 's'} · contract ${fmtDate(r.start)} – ${r.openEnded ? 'open' : fmtDate(r.end)}`}>
                <View style={{
                    position: 'absolute', left: spanLeft, top: (ROW_H - 2) / 2, width: spanW, height: 2,
                    backgroundColor: C_RAIL,
                }} />
                {ivs.map((iv, i) => {
                    const left = Math.min(Math.max(timeline.xOf(iv.start), 0), timeline.width);
                    const right = Math.min(Math.max(timeline.xOf(iv.end), 0), timeline.width);
                    const w = Math.max(right - left, 4);
                    const crew = Math.round(iv.peakCrew);
                    return (
                        <View key={i}
                            title={`Interval ${i + 1} of ${ivs.length}: ${iv.start.format('M/D/YY')} – ${iv.end.format('M/D/YY')}`
                                + ` · ${iv.weeks} wk${iv.weeks === 1 ? '' : 's'} · peak ${crew} crew`
                                + ` · ${Math.round(iv.hours).toLocaleString()} hrs · ${formatMoney(iv.cost)}`}
                            style={{
                                position: 'absolute', left, top: (ROW_H - BAR_H) / 2, width: w, height: BAR_H,
                                backgroundColor: color, borderRadius: 3, justifyContent: 'center',
                                alignItems: 'center', overflow: 'hidden',
                            }}>
                            {w >= 18 && crew > 0 && (
                                <Text style={{ fontSize: 9, color: '#FFFFFF', whiteSpace: 'nowrap' }}>{crew}</Text>
                            )}
                        </View>
                    );
                })}
            </View>
        );
    };

    // Thin bar spanning a crew member's first-to-last labor charge. Clamped to
    // the timeline window — old jobs can have labor before rangeStart.
    const CREW_ROW_H = 18, CREW_BAR_H = 8;
    const crewBar = (p) => {
        if (!isReal(p.first) || !isReal(p.last)) return <View style={{ width: timeline.width, height: CREW_ROW_H }} />;
        const left = Math.min(Math.max(timeline.xOf(p.first), 0), timeline.width);
        const right = Math.min(Math.max(timeline.xOf(p.last), 0), timeline.width);
        const w = Math.max(right - left, 4);
        const color = p.role === 'SUPERINTENDENT' ? '#475569' : '#94A3B8';
        return (
            <View style={{ position: 'relative', height: CREW_ROW_H, width: timeline.width }}
                title={`${p.name}: ${p.first.format('M/D/YY')} – ${p.last.format('M/D/YY')} · ${Math.round(p.hours)}h`}>
                <View style={{
                    position: 'absolute', left, top: (CREW_ROW_H - CREW_BAR_H) / 2, width: w, height: CREW_BAR_H,
                    backgroundColor: color, borderRadius: 3,
                }} />
            </View>
        );
    };

    const filterSelect = (label, value, setValue, options) => (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 12 }}>
            <Text style={{ fontSize: 12, color: '#374151', marginRight: 5 }}>{label}</Text>
            <select value={value} onChange={(e) => setValue(e.target.value)}
                style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid ' + C_BORDER, backgroundColor: '#FFFFFF', color: '#111827', maxWidth: 180 }}>
                <option value="ALL">All {label.toLowerCase()}s</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </View>
    );

    return (
        <View style={{ flex: 1, padding: 16, backgroundColor: '#F5F7FA' }}>
            {/* Header — title, filters, legend, and export condensed onto one row */}
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Text title="Active projects by Project Manager — click a project to open its forecast; click the ▸ caret for actual field crew"
                        style={{ fontSize: 16, fontWeight: '700', color: '#111827', marginRight: 8 }}>Team Schedule</Text>
                    <Text style={{ fontSize: 11, color: '#6B7280', marginRight: 16 }}>{rows.length} project{rows.length === 1 ? '' : 's'}</Text>
                    {filterSelect('Team', teamFilter, (v) => { setTeamFilter(v); setPmFilter('ALL'); setSuperFilter('ALL'); }, teamOptions)}
                    {filterSelect('PM', pmFilter, (v) => { setPmFilter(v); setSuperFilter('ALL'); }, pmOptions)}
                    {filterSelect('Super', superFilter, setSuperFilter, superOptions)}
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 12 }}>
                        <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: C_ACTIVE, marginRight: 4 }} />
                        <Text style={{ fontSize: 11, color: '#6B7280' }}>Active</Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 12 }}>
                        <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: C_PENDING, marginRight: 4 }} />
                        <Text style={{ fontSize: 11, color: '#6B7280' }}>Pending</Text>
                    </View>
                    <Text style={{ fontSize: 11, color: '#9CA3AF', marginRight: 12 }}>▸ no completion date</Text>
                    {/* Labor intervals: one bar per mobilization from the burn tool,
                        over a thin rail for the contract span. Off = one bar per job. */}
                    <label title="Draw one bar per staffed labor window from the Burn Tool instead of a single contract-span bar. The number on a bar is that window's peak crew."
                        style={{ display: 'flex', alignItems: 'center', marginRight: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={showIntervals} onChange={(e) => setShowIntervals(e.target.checked)}
                            style={{ marginRight: 4 }} />
                        <Text style={{ fontSize: 11, color: '#6B7280' }}>Labor intervals</Text>
                    </label>
                    {showIntervals && (
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 12 }}>
                            <View style={{ width: 12, height: 2, backgroundColor: C_RAIL, marginRight: 4 }} />
                            <Text style={{ fontSize: 11, color: '#9CA3AF' }}>contract span</Text>
                        </View>
                    )}
                    {!rosterLoaded && <Text style={{ fontSize: 11, color: '#9CA3AF', marginRight: 8 }}>loading values…</Text>}
                    {showIntervals && !intervalsLoaded && <Text style={{ fontSize: 11, color: '#9CA3AF' }}>loading labor…</Text>}
                </View>
                <Pressable
                    onPress={exportExcel}
                    disabled={rows.length === 0}
                    style={{
                        flexDirection: 'row', alignItems: 'center', paddingLeft: 10, paddingRight: 10, paddingTop: 4, paddingBottom: 4,
                        borderRadius: 6, backgroundColor: rows.length === 0 ? '#E5E7EB' : '#065F46',
                    }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: rows.length === 0 ? '#9CA3AF' : '#FFFFFF' }}>Export Excel</Text>
                </Pressable>
            </View>

            {rows.length === 0 ? (
                <View style={{ padding: 40, alignItems: 'center' }}>
                    <Text style={{ fontSize: 14, color: '#6B7280' }}>
                        {jobs.length ? 'No active projects match these filters.' : 'Loading projects…'}
                    </Text>
                </View>
            ) : (
                /* contain: inline-size keeps the grid's intrinsic width from inflating the
                   page (main content pane is flex:1 with no min-width:0) — without it the
                   BODY scrolls horizontally instead of this container, and the sticky left
                   panel never engages. */
                <View style={{ backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: C_BORDER, borderRadius: 8, overflow: 'auto', maxHeight: 'calc(100vh - 180px)', contain: 'inline-size' }}>
                    <View style={{ minWidth: LEFT_PANEL + timeline.width, position: 'relative' }}>
                        {/* Column header — frozen at the top while rows scroll under it */}
                        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: C_BORDER, backgroundColor: C_HEADER, position: 'sticky', top: 0, zIndex: 4 }}>
                            <View style={{ ...leftPanel(C_HEADER) }}>
                                <View style={col(COL_JOB, { paddingLeft: 28 })}><Text style={hcell}>JOB</Text></View>
                                <View style={col(COL_PROJ)}><Text style={hcell}>PROJECT</Text></View>
                                <View style={col(COL_START)}><Text style={hcell}>START</Text></View>
                                <View style={col(COL_STOP)}><Text style={hcell}>STOP</Text></View>
                                <View style={col(COL_CV)}><Text style={hcell}>CV REV</Text></View>
                                <View style={col(COL_BACK)}><Text style={hcell}>BACKLOG</Text></View>
                            </View>
                            <View style={{ position: 'relative', flexDirection: 'row', height: 30 }}>
                                {timeline.monthLabels.map((label, i) => (
                                    <View key={i} style={{ width: PX_PER_MONTH, height: 30, justifyContent: 'center', alignItems: 'center', borderRightWidth: 1, borderRightColor: C_BORDER }}>
                                        <Text style={{ fontSize: 10, color: '#6B7280' }}>{label}</Text>
                                    </View>
                                ))}
                                <View style={{ position: 'absolute', left: timeline.todayX, top: 0, width: 2, height: 30, backgroundColor: C_TODAY }} />
                            </View>
                        </View>

                        {/* Groups */}
                        {groups.map((g) => (
                            <View key={g.pm}>
                                {/* PM header */}
                                <View style={{ flexDirection: 'row', backgroundColor: '#EEF2FF', borderBottomWidth: 1, borderBottomColor: C_BORDER }}>
                                    <View style={{ ...leftPanel('#EEF2FF'), alignItems: 'center', paddingLeft: 8, paddingRight: 8, justifyContent: 'space-between' }}>
                                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#3730A3' }}>{g.pm}</Text>
                                        <Text style={{ fontSize: 11, color: '#6366F1', paddingRight: 8 }}>{g.items.length} · {formatMoney(g.totalCv)}</Text>
                                    </View>
                                    <View style={{ width: timeline.width, backgroundColor: '#EEF2FF' }} />
                                </View>

                                {/* Project rows */}
                                {g.items.map((r, ri) => {
                                    const isOpen = !!expanded[r.id];
                                    const crew = crewByJob[r.id];
                                    // Light zebra banding (alternates within each PM group)
                                    const rowBg = ri % 2 === 1 ? C_BAND : '#FFFFFF';
                                    return (
                                        <View key={r.id}>
                                            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#F3F4F6', backgroundColor: rowBg }}>
                                                <View style={{ ...leftPanel(rowBg) }}>
                                                    {/* Caret toggles the actual field-crew panel */}
                                                    <Pressable onPress={() => toggleExpand(r)} title="Show field crew" style={{ width: 20, justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                                                        <Text style={{ fontSize: 12, color: '#6B7280' }}>{isOpen ? '▾' : '▸'}</Text>
                                                    </Pressable>
                                                    {/* Clicking the project selects it and opens its Forecast */}
                                                    <Pressable onPress={() => selectJobAndForecast(r)}
                                                        title={`${r.team}${r.superintendent ? ' · ' + r.superintendent : ''}`
                                                            + (r.intervals.length > 1 ? ` · ${r.intervals.length} labor intervals` : '')
                                                            + ' — open forecast'}
                                                        style={{ flexDirection: 'row', flex: 1, cursor: 'pointer' }}>
                                                        <View style={col(COL_JOB - 20)}>
                                                            <Text style={{ ...dcell, color: '#6B7280' }}>{r.id}</Text>
                                                        </View>
                                                        <View style={col(COL_PROJ, { overflow: 'hidden' })}>
                                                            <Text style={{ display: 'block', maxWidth: '100%', fontSize: 12, lineHeight: '14px', fontWeight: '600', color: '#1D4ED8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {r.name}
                                                            </Text>
                                                        </View>
                                                        {/* Date cells open the edit-dates modal instead of the forecast */}
                                                        <Pressable onPress={(e) => { e && e.stopPropagation && e.stopPropagation(); openDateModal(r); }}
                                                            title="Edit dates" style={col(COL_START)}>
                                                            <Text style={dateCell}>{fmtDate(r.start)}</Text>
                                                        </Pressable>
                                                        <Pressable onPress={(e) => { e && e.stopPropagation && e.stopPropagation(); openDateModal(r); }}
                                                            title="Edit dates" style={col(COL_STOP)}>
                                                            <Text style={dateCell}>{fmtDate(r.end && !r.openEnded ? r.end : null)}</Text>
                                                        </Pressable>
                                                        <View style={col(COL_CV)}><Text style={{ ...dcell, fontWeight: '600' }}>{formatMoney(r.cv)}</Text></View>
                                                        <View style={col(COL_BACK)}>
                                                            <Text style={{ ...dcell, color: r.backlog != null && r.backlog < 0 ? C_NEG : '#111827' }}>{formatMoney(r.backlog)}</Text>
                                                        </View>
                                                    </Pressable>
                                                </View>
                                                {bar(r)}
                                            </View>

                                            {/* Expandable actual-crew panel — one row per person so each
                                                gets a Gantt bar (first-to-last labor charge) on the timeline */}
                                            {isOpen && (
                                                <View style={{ borderBottomWidth: 1, borderBottomColor: '#F3F4F6', backgroundColor: '#FAFAFA', paddingTop: 4, paddingBottom: 6 }}>
                                                    <View style={{ flexDirection: 'row' }}>
                                                        <View style={{ ...leftPanel('#FAFAFA'), paddingLeft: 12, paddingRight: 12, paddingBottom: 4 }}>
                                                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#374151' }}>
                                                                Field crew (from actual labor)
                                                            </Text>
                                                        </View>
                                                        <View style={{ width: timeline.width }} />
                                                    </View>
                                                    {(!crew || crew.loading || crew.error || crew.people.length === 0) ? (
                                                        <View style={{ flexDirection: 'row' }}>
                                                            <View style={{ ...leftPanel('#FAFAFA'), paddingLeft: 12, paddingRight: 12 }}>
                                                                <Text style={{ fontSize: 11, color: '#9CA3AF' }}>
                                                                    {!crew || crew.loading ? 'Loading crew…'
                                                                        : crew.error ? crew.error
                                                                            : 'No superintendents or foremen charged labor yet.'}
                                                                </Text>
                                                            </View>
                                                            <View style={{ width: timeline.width }} />
                                                        </View>
                                                    ) : (
                                                        crew.people.map((p, i) => (
                                                            <View key={i} style={{ flexDirection: 'row' }}>
                                                                <View style={{ ...leftPanel('#FAFAFA'), paddingLeft: 12, paddingRight: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Text title={`${p.name} · ${p.role === 'SUPERINTENDENT' ? 'Super' : 'Foreman'}`}
                                                                        style={{ flex: 1, minWidth: 0, fontSize: 11, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                        {p.name} <Text style={{ color: '#6B7280' }}>· {p.role === 'SUPERINTENDENT' ? 'Super' : 'Foreman'}</Text>
                                                                    </Text>
                                                                    <Text style={{ fontSize: 11, color: '#6B7280', paddingLeft: 8, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                                        {Math.round(p.hours)}h
                                                                    </Text>
                                                                </View>
                                                                {crewBar(p)}
                                                            </View>
                                                        ))
                                                    )}
                                                </View>
                                            )}
                                        </View>
                                    );
                                })}
                            </View>
                        ))}

                        {/* Today marker — spans the full grid height, above the bars.
                            zIndex 1 keeps it under the sticky left panel (2) and header (4). */}
                        <View style={{ position: 'absolute', left: LEFT_PANEL + timeline.todayX, top: 0, bottom: 0, width: 2, backgroundColor: C_TODAY, zIndex: 1, pointerEvents: 'none' }} />
                    </View>
                </View>
            )}

            {/* Edit-dates modal — same fields the top-menu date modal saves
                (Forecast_User_Data on the selected version), for any row's job. */}
            {dateModal && (
                <Modal visible={true} onRequestClose={() => setDateModal(null)}
                    style={{ padding: 24, minWidth: 340, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
                    <View>
                            <Text style={{ fontSize: 16, fontWeight: '700', color: '#111827' }}>Edit Dates</Text>
                            <Text style={{ fontSize: 13, color: '#374151', marginTop: 2, marginBottom: 16 }}>
                                {dateModal.id} <Text style={{ color: '#6B7280' }}>· {dateModal.name}</Text>
                            </Text>

                            {dateModal.error ? (
                                <Text style={{ fontSize: 12, color: '#B45309', marginBottom: 12 }}>{dateModal.error}</Text>
                            ) : null}
                            {dateModal.locked && !dateModal.error ? (
                                <Text style={{ fontSize: 12, color: '#B45309', marginBottom: 12 }}>
                                    This forecast version is locked — dates are read-only.
                                </Text>
                            ) : null}
                            {dateModal.loading ? (
                                <Text style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12 }}>Loading saved dates…</Text>
                            ) : null}

                            <View style={{ marginBottom: 14 }}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 5 }}>Start Date - Actual</Text>
                                <DateInput value={dateModal.start} disabled={dateModal.loading || dateModal.locked}
                                    onValueChange={(v) => setDateModal((m) => (m ? { ...m, start: v } : m))} />
                            </View>
                            <View style={{ marginBottom: 14 }}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 5 }}>Complete Date - Forecast</Text>
                                <DateInput value={dateModal.end} disabled={dateModal.loading || dateModal.locked}
                                    onValueChange={(v) => setDateModal((m) => (m ? { ...m, end: v } : m))} />
                            </View>
                            <View style={{ marginBottom: 20 }}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 5 }}>Mech Complete</Text>
                                <DateInput value={dateModal.mech} disabled={dateModal.loading || dateModal.locked}
                                    onValueChange={(v) => setDateModal((m) => (m ? { ...m, mech: v } : m))} />
                            </View>

                            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12 }}>
                                <Pressable onPress={() => setDateModal(null)}
                                    style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, borderRadius: 6, backgroundColor: '#F3F4F6' }}>
                                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151' }}>Cancel</Text>
                                </Pressable>
                                <Pressable onPress={saveDateModal}
                                    disabled={dateModal.loading || dateModal.locked || dateModal.saving}
                                    style={{
                                        paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, borderRadius: 6,
                                        backgroundColor: (dateModal.loading || dateModal.locked || dateModal.saving) ? '#E5E7EB' : '#065F46',
                                    }}>
                                    <Text style={{ fontSize: 13, fontWeight: '600', color: (dateModal.loading || dateModal.locked || dateModal.saving) ? '#9CA3AF' : '#FFFFFF' }}>
                                        {dateModal.saving ? 'Saving…' : 'Save'}
                                    </Text>
                                </Pressable>
                            </View>
                    </View>
                </Modal>
            )}
        </View>
    );
};

const hcell = { fontSize: 10, fontWeight: '700', color: '#374151' };
const dcell = { fontSize: 11, color: '#111827', whiteSpace: 'nowrap' };
// Dotted underline signals the Start/Stop cells are clickable (edit dates)
const dateCell = { ...dcell, textDecorationLine: 'underline', textDecorationStyle: 'dotted', textDecorationColor: '#9CA3AF' };

export default TeamGantt;
